import { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import {
  type Workspace,
  WorkspaceInitialized,
} from '@affine/core/modules/workspace';
import { WorkspaceService } from '@affine/core/modules/workspace/services/workspace';
import { isWorkspaceBlobLocalStorageUnavailableError } from '@affine/core/modules/workspace/utils/blob-cache';
import {
  isWorkspaceBlobHashMismatchError,
  stageWorkspaceFile,
  uploadStagedWorkspaceFileViaTus,
} from '@affine/core/modules/workspace/utils/tus-direct-upload';
import type { UploadProgressCallback } from '@blocksuite/affine/shared/services';
import { LiveData, OnEvent, Service } from '@toeverything/infra';
import { Subject } from 'rxjs';

import type {
  BlobUploadMutationPayload,
  OfflineMutationRecord,
} from '../types';
import {
  OfflineMutationDomainError,
  OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from './offline-mutation-outbox';

export interface StageWorkspaceBlobOptions {
  docId?: string;
  onProgress?: UploadProgressCallback;
}

/**
 * Bridges the durable OPFS blob cache to the generic metadata outbox.
 * Binary data never enters IndexedDB: retries read the single OPFS copy and
 * TUS remains the only network transport.
 */
@OnEvent(
  WorkspaceInitialized,
  (service: WorkspaceBlobUploadOutboxService) => service.onWorkspaceInitialized
)
export class WorkspaceBlobUploadOutboxService extends Service {
  readonly pendingUploads$: LiveData<OfflineMutationRecord<'blob.upload'>[]>;
  readonly failedUploads$: LiveData<OfflineMutationRecord<'blob.upload'>[]>;
  readonly completed$ = new Subject<OfflineMutationRecord<'blob.upload'>>();

  private readonly progressCallbacks = new Map<
    string,
    UploadProgressCallback
  >();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService
  ) {
    super();
    this.pendingUploads$ = LiveData.computed(get =>
      get(this.outbox.pendingMutations$).filter(isBlobUploadMutation)
    );
    this.failedUploads$ = LiveData.computed(get =>
      get(this.outbox.failedMutations$).filter(isBlobUploadMutation)
    );
    this.disposables.push(
      this.outbox.registerExecutor('blob.upload', mutation =>
        this.uploadStagedBlob(mutation)
      )
    );
    const completedSubscription = this.outbox.completed$.subscribe(mutation => {
      if (isBlobUploadMutation(mutation)) {
        this.completed$.next(mutation);
      }
    });
    this.disposables.push(() => completedSubscription.unsubscribe());
    this.disposables.push(() => this.completed$.complete());
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    // The event decorator eagerly constructs this workspace-scoped service.
    // Executor registration in the constructor is the required side effect.
    void _workspace;
  }

  async stage(
    file: File,
    options: StageWorkspaceBlobOptions = {}
  ): Promise<string> {
    const workspace = this.workspaceService.workspace;
    if (workspace.flavour === 'local') {
      throw new OfflineMutationDomainError(
        'Workspace blob outbox requires a cloud workspace'
      );
    }

    const { blobKey, file: stagedFile } = await stageWorkspaceFile({
      workspaceId: workspace.id,
      file,
    });
    const payload: BlobUploadMutationPayload = {
      workspaceId: workspace.id,
      blobKey,
      docId: options.docId,
      mimeType: stagedFile.type || 'application/octet-stream',
      size: stagedFile.size,
    };

    const idempotencyKey = blobUploadIdempotencyKey(payload);
    if (options.onProgress) {
      this.progressCallbacks.set(idempotencyKey, options.onProgress);
    }

    try {
      await this.outbox.enqueue({
        operation: 'blob.upload',
        payload,
        idempotencyKey,
        dedupePending: true,
      });
    } catch (error) {
      this.progressCallbacks.delete(idempotencyKey);
      throw error;
    }
    return blobKey;
  }

  async retryFailedUploads(): Promise<void> {
    for (const mutation of this.failedUploads$.value) {
      await this.outbox.retryFailed(mutation.id);
    }
  }

  private async uploadStagedBlob(
    mutation: OfflineMutationRecord<'blob.upload'>
  ): Promise<void> {
    const { payload } = mutation;
    if (payload.workspaceId !== this.workspaceService.workspace.id) {
      throw new OfflineMutationDomainError(
        'Blob upload mutation belongs to another workspace'
      );
    }

    try {
      const blobKey = await uploadStagedWorkspaceFileViaTus({
        workspaceId: payload.workspaceId,
        blobKey: payload.blobKey,
        docId: payload.docId,
        onProgress: this.progressCallbacks.get(mutation.idempotencyKey),
        serverBaseUrl: this.workspaceServerService.server?.baseUrl,
      });
      if (!blobKey) {
        throw new OfflineMutationDomainError(
          'TUS upload is disabled for this workspace'
        );
      }
      if (blobKey !== payload.blobKey) {
        throw new OfflineMutationDomainError(
          'TUS upload returned a mismatched content hash'
        );
      }
    } catch (error) {
      if (isWorkspaceBlobLocalStorageUnavailableError(error)) {
        throw new OfflineMutationDomainError(
          'The local upload copy is no longer available'
        );
      }
      if (isWorkspaceBlobHashMismatchError(error)) {
        throw new OfflineMutationDomainError(error.message);
      }
      if (error instanceof OfflineMutationDomainError) {
        throw error;
      }
      const status = tusErrorStatus(error);
      if (
        status >= 400 &&
        status < 500 &&
        status !== 408 &&
        status !== 425 &&
        status !== 429
      ) {
        throw new OfflineMutationDomainError(errorMessage(error));
      }
      throw new OfflineMutationTransientError(errorMessage(error));
    } finally {
      this.progressCallbacks.delete(mutation.idempotencyKey);
    }
  }
}

function blobUploadIdempotencyKey(payload: BlobUploadMutationPayload): string {
  return `blob.upload:${payload.workspaceId}:${payload.blobKey}:${payload.docId ?? ''}`;
}

function isBlobUploadMutation(
  mutation: OfflineMutationRecord
): mutation is OfflineMutationRecord<'blob.upload'> {
  return mutation.operation === 'blob.upload';
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function tusErrorStatus(error: unknown): number {
  if (!error || typeof error !== 'object') {
    return 0;
  }
  const response = (
    error as {
      originalResponse?: { getStatus?: () => unknown };
    }
  ).originalResponse;
  const status = response?.getStatus?.();
  return typeof status === 'number' ? status : 0;
}
